/**
 * The custom tool builder as a compound: <CustomToolBuilder> holds the draft, the stage, the focused
 * step, the slug check, the suggestion and the save. Header guides from one stage to the next, Build
 * shows the steps as a canvas or as blocks, and Details names the finished steps.
 */

import { useCallback, useEffect, useReducer, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useAtom } from 'jotai';
import { isAxiosError } from 'axios';
import { toast } from '@/components/ui/sonner';
import { getApiErrorMessage } from '@/lib/utils';
import { schemaFields } from '@/modules/back-testing/datasets/tools/tool-schema';
import type { ToolDefinitionAction } from '@/services/toolDefinition.service';
import type { CustomTool, CustomToolStep } from '@/types/custom-tool.types';
import {
	buildIssue,
	detailsProblem,
	draftFromTool,
	emptyDraft,
	sameDraft,
	slugFormatProblem,
	toPayload,
	withStep,
	withSuggestion,
	type CustomToolDraft
} from '../custom-tool-draft';
import { customToolActionPath } from '../paths';
import {
	useCreateCustomTool,
	useSlugStatus,
	useSuggestCustomTool,
	useUpdateCustomTool,
	type SlugStatus
} from '../use-custom-tools';
import { isWriteTool, useStepTools } from '../use-step-tools';
import {
	CustomToolBuilderContext,
	useCustomToolBuilder,
	type BuilderStage,
	type BuilderFocus,
	type CustomToolBuilderValue,
	type StepOutcomes
} from './builder-context';
import { BuildStage } from './build-stage';
import { builderViewAtom } from './builder-view';
import { draftHistory, startHistory } from './draft-history';
import { DetailsStage } from './details-stage';
import { LeaveDialog } from './leave-dialog';
import { StageHeader } from './stage-header';
import { useLeaveGuard } from './use-leave-guard';

const slugProblem = (status: SlugStatus): string | null => {
	if (status === 'checking') return 'Checking the slug';
	if (status === 'idle' || status === 'available' || status === 'failed') return null;
	return 'Pick another slug';
};

interface CustomToolBuilderProps {
	/** The saved tool to edit, or null to create one. */
	tool: CustomTool | null;
	children: ReactNode;
}

const Root = ({ tool, children }: CustomToolBuilderProps) => {
	const navigate = useNavigate();
	const [history, dispatch] = useReducer(draftHistory, tool, (saved) => startHistory(saved ? draftFromTool(saved) : emptyDraft()));
	const { draft } = history;
	// What the tool is now: the draft it opened with, until a save replaces it.
	const [saved, setSaved] = useState(history.draft);
	const changed = !sameDraft(draft, saved);
	const { blocker, allowLeave } = useLeaveGuard(changed);
	const change = useCallback(
		(edit: (current: CustomToolDraft) => CustomToolDraft, group?: string) => dispatch({ type: 'edit', edit, at: Date.now(), group }),
		[]
	);
	const [stage, setStage] = useState<BuilderStage>('build');
	const [storedView, setView] = useAtom(builderViewAtom);
	// Anything stored that is not a view opens the canvas.
	const view = storedView === 'blocks' ? 'blocks' : 'canvas';
	const [focus, setFocus] = useState<BuilderFocus>(null);
	const [testOpen, setTestOpen] = useState(false);
	const [outcomes, setOutcomes] = useState<StepOutcomes | null>(null);
	const autoSuggested = useRef(!!tool);
	const [saveError, setSaveError] = useState<{ message: string; slug: string | null } | null>(null);
	const stepTools = useStepTools();
	const slugStatus = useSlugStatus(draft.slug, !tool && stage === 'details' && !slugFormatProblem(draft.slug));
	const suggest = useSuggestCustomTool();
	const create = useCreateCustomTool();
	const update = useUpdateCustomTool();

	const fieldsOf = (step: CustomToolStep) => {
		const definition = stepTools.toolFor(step);
		return definition ? schemaFields(definition.inputSchema) : null;
	};
	const writes = draft.steps.some((step) => isWriteTool(stepTools.toolFor(step)));
	const issue = buildIssue(draft, fieldsOf);

	// A test result describes the steps it ran; once they change it no longer does.
	useEffect(() => setOutcomes(null), [draft.inputs, draft.steps]);

	// A removed step cannot stay focused, so taking the removal back does not bring the focus with it.
	useEffect(() => {
		if (focus?.kind === 'step' && !draft.steps.some((step) => step.id === focus.id)) setFocus(null);
	}, [draft.steps, focus]);

	const askSuggestion = async (from: CustomToolDraft, blanksOnly: boolean) => {
		try {
			const suggestion = await suggest.mutateAsync({ inputs: from.inputs, steps: from.steps });
			change((current) => withSuggestion(current, suggestion, { blanksOnly, slugLocked: !!tool }));
		} catch {
			// Shown from the mutation's own error state.
		}
	};

	const goTo = (next: BuilderStage) => {
		if (next === 'details' && issue) return;
		setStage(next);
		// The first time the steps are done, a name is already waiting.
		if (next === 'details' && !autoSuggested.current && !draft.name.trim()) {
			autoSuggested.current = true;
			void askSuggestion(draft, true);
		}
	};

	const addStep = (definition: ToolDefinitionAction) => {
		const added = withStep(draft, definition.provider, definition.tool);
		change(() => added);
		setFocus({ kind: 'step', id: added.steps[added.steps.length - 1].id });
	};

	const saveProblem = (): string | null => {
		const details = detailsProblem(draft, !!tool);
		if (details) return details;
		if (!tool) return slugProblem(slugStatus);
		return changed ? null : 'No changes to save';
	};
	const problem = saveProblem();

	const save = async () => {
		if (issue || problem) return;
		setSaveError(null);
		const payload = toPayload(draft);
		try {
			if (tool) {
				const { name, description, inputs, steps } = payload;
				await update.mutateAsync({ id: tool.id, update: { name, description, inputs, steps } });
				setSaved(draft);
				toast.success('Custom tool saved');
			} else {
				await create.mutateAsync(payload);
				toast.success('Custom tool created. Try it here.');
				allowLeave();
				// In place of the new-tool page, so going back lands on the list rather than an empty builder.
				navigate(customToolActionPath(payload.slug), { state: { tab: 'test' }, replace: true });
			}
		} catch (err) {
			const slugTaken = isAxiosError(err) && err.response?.status === 409;
			setSaveError({
				message: getApiErrorMessage(err, slugTaken ? 'This slug is taken' : 'Could not save the custom tool'),
				slug: slugTaken ? payload.slug : null
			});
		}
	};

	const value: CustomToolBuilderValue = {
		draft,
		toolId: tool?.id ?? null,
		change,
		undo: { run: () => dispatch({ type: 'undo' }), available: history.past.length > 0 },
		stage,
		view,
		setView,
		goTo,
		focus,
		focusOn: setFocus,
		addStep,
		stepTools,
		fieldsOf,
		writes,
		buildIssue: issue,
		testing: { open: testOpen, setOpen: setTestOpen, outcomes, setOutcomes },
		slugStatus,
		suggestion: {
			ask: () => void askSuggestion(draft, false),
			pending: suggest.isPending,
			failed: suggest.isError,
			succeeded: suggest.isSuccess
		},
		save: {
			run: () => void save(),
			pending: create.isPending || update.isPending,
			problem,
			error: saveError && !saveError.slug ? saveError.message : null,
			// Until the slug it was about is edited.
			slugError: saveError?.slug && saveError.slug === draft.slug ? saveError.message : null
		}
	};

	return (
		<CustomToolBuilderContext.Provider value={value}>
			{children}
			<LeaveDialog blocker={blocker} editing={!!tool} />
		</CustomToolBuilderContext.Provider>
	);
};

const Stage = () => {
	const { stage } = useCustomToolBuilder();
	return stage === 'build' ? <BuildStage /> : <DetailsStage />;
};

export const CustomToolBuilder = Object.assign(Root, {
	Header: StageHeader,
	Stage
});
